import React from "react";
import { motion } from "framer-motion";
import { ALMANAC, ASSETS } from "@/data/content";
import { Reveal } from "@/lib/reveal";

const Frame = ({ src, alt, caption, className = "", imgClass = "" }) => (
 <figure className={`group ${className}`}>
 <div className="rounded-[3px] border-[10px] border-paper bg-paper shadow-[0_18px_50px_rgba(42,32,21,0.28)] transition-transform duration-500 group-hover:scale-[1.02]">
 <img src={src} alt={alt} className={`block h-full w-full object-cover ${imgClass}`} />
 </div>
 {caption && <figcaption className="mt-2 text-center font-script text-lg text-ink/70">{caption}</figcaption>}
 </figure>
);

export const Almanac = () => (
 <section id="almanac" data-testid="almanac" className="relative overflow-hidden bg-cream py-24 md:py-32">
 <div className="mx-auto max-w-[1400px] px-5 md:px-10">
 {/* Header, watercolour feature + woven quote */}
 <div className="grid items-center gap-10 md:grid-cols-[0.8fr_1.2fr]">
 <Reveal>
 <div className="relative flex justify-center">
 <div className="absolute bottom-4 h-6 w-3/4 rounded-[100%] bg-ink/12 blur-lg" />
 <img src={ASSETS.wcAkubra} alt="Watercolour of a Carbon8 farmer" className="relative h-72 w-auto object-contain drop-shadow-[0_22px_30px_rgba(42,32,21,0.25)] md:h-96" />
 </div>
 </Reveal>
 <Reveal delay={0.05}>
 <span className="font-body text-xs uppercase tracking-[0.3em] text-ochre">{ALMANAC.kicker}</span>
 <h2 className="mt-3 font-display text-4xl font-medium leading-[1.02] text-ink sm:text-5xl lg:text-6xl">{ALMANAC.heading}</h2>
 <p className="mt-5 max-w-xl font-body text-base leading-relaxed text-ink/75">{ALMANAC.body}</p>
 <blockquote className="mt-7 border-l-2 border-terracotta pl-5">
 <p className="font-display text-2xl font-medium italic text-forest">“You can't decouple land health from human health.”</p>
 <footer className="mt-2 font-script text-xl text-sage">Nicole Masters</footer>
 </blockquote>
 </Reveal>
 </div>

 {/* Desktop scrapbook collage, community photography + watercolours */}
 <div className="relative mt-16 hidden h-[820px] lg:block">
 <motion.div initial={{ opacity: 0, y: 30, rotate: -4 }} whileInView={{ opacity: 1, y: 0, rotate: -2.5 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
 className="absolute left-0 top-10 z-10 w-[40%]">
 <Frame src={ASSETS.soilPit} alt="The soil pit demonstration" caption="the soil pit, everyone leans in" imgClass="aspect-[4/3]" />
 </motion.div>

 <motion.div initial={{ opacity: 0, y: 30, rotate: 4 }} whileInView={{ opacity: 1, y: 0, rotate: 3 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
 className="absolute right-[4%] top-0 z-20 w-[28%]">
 <Frame src={ASSETS.gathering} alt="Community gathered outside a country hall" caption="lunch, outside the hall" imgClass="aspect-[3/4]" />
 </motion.div>

 <motion.div initial={{ opacity: 0, y: 30, rotate: -5 }} whileInView={{ opacity: 1, y: 0, rotate: -3.5 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.18 }}
 className="absolute right-[2%] top-[48%] z-10 w-[32%]">
 <Frame src={ASSETS.mikeTeach} alt="Reading the ground together" caption="reading the ground" imgClass="aspect-[3/2]" />
 </motion.div>

 <motion.div initial={{ opacity: 0, y: 30, rotate: 3 }} whileInView={{ opacity: 1, y: 0, rotate: 2 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.24 }}
 className="absolute bottom-4 left-[24%] z-30 w-[22%]">
 <Frame src={ASSETS.fieldWalk} alt="Walking the paddock" imgClass="aspect-square" />
 </motion.div>

 {/* Watercolours integrated (feature, no frame) */}
 <motion.img initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.2 }}
 src={ASSETS.wcSmile} alt="Watercolour of a Carbon8 grower"
 className="pointer-events-none absolute bottom-0 left-[46%] z-40 h-[58%] w-auto object-contain drop-shadow-[0_20px_30px_rgba(42,32,21,0.2)]" />
 <motion.img initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.3 }}
 src={ASSETS.wcWatch} alt="Watercolour of a Carbon8 grower"
 className="pointer-events-none absolute -bottom-2 right-[6%] z-40 h-[46%] w-auto object-contain drop-shadow-[0_20px_30px_rgba(42,32,21,0.2)]" />

 {/* Field note */}
 <motion.div initial={{ opacity: 0, rotate: -6 }} whileInView={{ opacity: 1, rotate: -4 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.3 }}
 className="absolute bottom-28 left-0 z-40 w-[22%] rounded-sm border border-clay bg-paper p-5 shadow-[0_10px_30px_rgba(42,32,21,0.15)]">
 <span className="tape absolute -top-3 left-1/2 h-6 w-20 -translate-x-1/2 rounded-sm" />
 <p className="font-script text-2xl leading-tight text-terracotta">"the truth is hidden in Country…"</p>
 <p className="mt-2 font-body text-xs uppercase tracking-wider text-ink/50">field note · no. 04</p>
 </motion.div>
 </div>

 {/* Mobile / tablet */}
 <div className="mt-12 grid gap-8 lg:hidden">
 <Frame src={ASSETS.soilPit} alt="The soil pit demonstration" caption="the soil pit, everyone leans in" className="-rotate-1" imgClass="aspect-[4/3]" />
 <div className="grid grid-cols-2 gap-4">
 <Frame src={ASSETS.gathering} alt="Community outside hall" className="rotate-1" imgClass="aspect-[3/4]" />
 <div className="relative">
 <Frame src={ASSETS.mikeTeach} alt="Reading the ground" className="-rotate-1" imgClass="aspect-[3/4]" />
 <img src={ASSETS.wcSmile} alt="" aria-hidden className="pointer-events-none absolute -bottom-6 -right-4 h-28 w-auto object-contain" />
 </div>
 </div>
 <div className="rounded-sm border border-clay bg-paper p-5 -rotate-1">
 <p className="font-script text-2xl text-terracotta">"the truth is hidden in Country…"</p>
 <p className="mt-1 font-body text-xs uppercase tracking-wider text-ink/50">field note · no. 04</p>
 </div>
 </div>
 </div>
 </section>
);
